import { Box, Button, Flex, Image, Text, TextButton } from '@modusign/design-system';
import { useStartSubscriptionWithTrialPlan } from '@modusign/openapi-client-subscription';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

import newTrialPlanThumbnail from '@app/assets/modal/welcomeModal/new-trial-plan-thumbnail.png';
import subscriptionThumbnailWithOutCountdown from '@app/assets/modal/welcomeModal/subscription-thumbnail-without-countdown.png';
import { PLAN_TRIAL } from '@app/features/subscription/constants';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import useTourMode from '@app/hooks/useTourMode';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import { isKakaoInAppBrowser } from '@app/libs/browserUserAgent';
import { modalClose } from '@app/store/modal/actions';
import { getProviders } from '@app/store/user/user/selectors';

function WelcomeChoiceModal() {
  const dispatch = useDispatch();
  const history = useHistory();
  const workspaceId = useWorkspaceId();
  const { createErrorHandler } = useOpenErrorModal();

  const [displayTourModeModal, setDisplayTourModeModal] = useState(false);
  const [, setTourMode] = useTourMode();

  const providers = useSelector(getProviders);

  const isKakaoInAppBrowserAndKakaoUser =
    isKakaoInAppBrowser() && providers.some((p) => p.type === 'kakao');

  const { mutate: startTrial, isLoading: isStartTrialLoading } = useStartSubscriptionWithTrialPlan({
    mutation: {
      onSuccess: () => {
        pushDataLayer({ event: 'getTrial' });
        history.go(0);
      },
      onError: () => {
        createErrorHandler({ title: '무료 체험 시작에 실패했습니다.' });
      },
    },
  });

  const clickStartTrial = useCallback(() => {
    if (isStartTrialLoading) {
      return;
    }
    pushDataLayer({ event: 'clickGetTrial' });
    startTrial({ workspaceId, data: { planId: PLAN_TRIAL } });
  }, [isStartTrialLoading, startTrial, workspaceId]);

  useEffect(() => {
    pushDataLayer({ event: 'viewWelcomeModal' });
  }, []);

  if (displayTourModeModal) {
    return (
      <Flex
        alignItems="center"
        height="100vh"
        justifyContent="center"
        my={[null, '-40px', '-66px']}
      >
        <Box backgroundColor="white" borderRadius="6px" lineHeight={1.5} p={9} textAlign="center">
          <Box fontSize="22px" fontWeight="bold">
            완료 문서 확인 외<br />
            기능 이용이 제한됩니다
          </Box>
          <Box mt={7}>
            1개월 무료 체험 선택 시 자동 결제 없이
            <br />
            모든 기능을 이용하실 수 있습니다.
          </Box>
          <Flex mt={9}>
            <Button
              sx={{ pointerEvents: 'auto' }}
              onClick={() => {
                setDisplayTourModeModal(false);
              }}
            >
              돌아가기
            </Button>
            <Button
              ml={3}
              sx={{ pointerEvents: 'auto' }}
              variant="primary"
              onClick={() => {
                pushDataLayer({ event: 'clickStartTourMode' });
                setTourMode(true);
                dispatch(modalClose());
              }}
            >
              그래도 둘러볼게요
            </Button>
          </Flex>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      height="100vh"
      my={[null, '-40px', '-66px']}
      overflowY="auto"
      px={[null, 7]}
      py={[null, 6]}
    >
      <Flex
        alignItems="center"
        backgroundColor="white"
        borderRadius={[null, '6px']}
        flex={1}
        flexDirection="column"
        lineHeight={1.5}
      >
        <Box
          backgroundColor="pureBlue500"
          borderTopLeftRadius={[null, '6px']}
          borderTopRightRadius={[null, '6px']}
          color="white"
          pb={8}
          pt={[7, 9]}
          px={[7, '40px']}
          textAlign="center"
          width="100%"
        >
          <Box fontSize="18px" fontWeight="medium">
            워크스페이스 설정이 완료되었어요!
          </Box>
          <Box mt={3}>이제 모두싸인 이용 방법을 선택해 볼게요.</Box>
        </Box>
        <Flex flex={1} flexDirection="column" maxWidth="584px" py={[9, 10]} width="100%">
          <Box flex={1} px={[7, 0]}>
            <Box textAlign="center">
              <Text fontSize={2} fontWeight="medium">
                제공된 2개의 가입 혜택 중 1개를 선택해 주세요.
              </Text>
            </Box>
            <Flex flexWrap="wrap" mt={7}>
              <Box
                flexBasis={['100%', null, 0]}
                flexGrow={1}
                flexShrink={1}
                sx={{
                  pointerEvents: 'none',
                  '&:hover': {
                    '.image': {
                      backgroundColor: 'amberYellow25',
                      borderColor: 'amberYellow400',
                    },
                    '.button': {
                      backgroundColor: 'pureYellow800',
                    },
                  },
                }}
                textAlign="center"
              >
                <Flex
                  backgroundColor="lightGray50"
                  borderColor="lightGray50"
                  borderStyle="solid"
                  borderWidth="1px"
                  className="image tagFreeTrialButton"
                  justifyContent="center"
                  pt={8}
                  sx={{
                    pointerEvents: [null, 'auto'],
                    cursor: [null, 'pointer'],
                  }}
                  onClick={clickStartTrial}
                >
                  <Image
                    alt="trial-plan"
                    display="block"
                    src={newTrialPlanThumbnail}
                    width="205px"
                  />
                </Flex>
                <Box mt={7}>무료로 제공되는 주요 기능을 이용해 볼게요.</Box>
                <Box mt="17px">
                  <Button
                    className="tagFreeTrialButton"
                    isLoading={isStartTrialLoading}
                    sx={{ pointerEvents: 'auto' }}
                    variant="primary"
                    onClick={clickStartTrial}
                  >
                    1개월 무료 체험
                  </Button>
                </Box>
              </Box>
              <Box
                flexBasis={['100%', null, 0]}
                flexGrow={1}
                flexShrink={1}
                ml={[0, null, 7]}
                mt={[9, null, 0]}
                sx={{
                  pointerEvents: 'none',
                  '&:hover': {
                    '.image': {
                      backgroundColor: 'amberYellow25',
                      borderColor: 'amberYellow400',
                    },
                    '.button': {
                      backgroundColor: 'pureYellow800',
                    },
                  },
                }}
                textAlign="center"
              >
                <Link
                  className="tagPaymentButton"
                  to="/payment/subscription"
                  onClick={() => pushDataLayer({ event: 'clickDirectPayment' })}
                >
                  <Flex
                    backgroundColor="lightGray50"
                    borderColor="lightGray50"
                    borderStyle="solid"
                    borderWidth="1px"
                    className="image"
                    justifyContent="center"
                    pt={8}
                    sx={{
                      pointerEvents: [null, 'auto'],
                      cursor: [null, 'pointer'],
                    }}
                  >
                    <Image
                      alt="subscription-pay"
                      display="block"
                      src={subscriptionThumbnailWithOutCountdown}
                      width="214px"
                    />
                  </Flex>
                </Link>
                <Box mt={7}>가입 할인 혜택받고 바로 시작할게요.</Box>
                <Box mt="17px">
                  <Link
                    className="tagPaymentButton"
                    to="/payment/subscription"
                    onClick={() => pushDataLayer({ event: 'clickDirectPayment' })}
                  >
                    <Button sx={{ pointerEvents: 'auto' }} variant="primary">
                      할인가로 이용
                    </Button>
                  </Link>
                </Box>
              </Box>
            </Flex>
          </Box>
          <Flex justifyContent="center" mt={11}>
            <TextButton
              onClick={() => {
                pushDataLayer({ event: 'clickShowTourModeModal' });
                setDisplayTourModeModal(true);
              }}
            >
              {isKakaoInAppBrowserAndKakaoUser
                ? '혜택 없이 완료 문서만 확인할게요'
                : '혜택 없이 둘러볼게요'}
            </TextButton>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default WelcomeChoiceModal;
